import { motion } from "framer-motion";
import { ArrowRight } from "@phosphor-icons/react";
import { useLang } from "../../context/LangContext";
import { IMAGES } from "../../lib/images";

const reveal = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

export const HowItWorks = () => {
  const { t } = useLang();
  return (
    <section id="how" className="relative bg-[#0A0F1A] py-24 lg:py-32 overflow-hidden" data-testid="how">
      <div
        className="absolute inset-0 opacity-[0.08]"
        style={{ backgroundImage: `url(${IMAGES.machineHall})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F1A] via-[#0A0F1A]/95 to-[#0A0F1A]" />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-12">
        <div className="max-w-2xl">
          <p className="font-mono-tech text-xs uppercase tracking-[0.2em] font-semibold text-emerald-500">[ {t.how.overline} ]</p>
          <h2 className="mt-4 font-display font-bold text-4xl sm:text-5xl tracking-tight text-white">{t.how.title}</h2>
        </div>

        <div className="mt-16 grid lg:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-md overflow-hidden">
          {t.how.steps.map((step, i) => (
            <motion.div
              key={i}
              custom={i}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className="relative bg-[#0A0F1A] p-8 lg:p-10 group hover:bg-[#0d1424] transition-colors duration-500"
              data-testid={`how-step-${i}`}
            >
              <div className="flex items-start justify-between">
                <span className="font-display font-bold text-7xl lg:text-8xl leading-none text-white">0{i + 1}</span>
                {i < t.how.steps.length - 1 && <ArrowRight size={24} className="mt-4 text-emerald-500 hidden lg:block opacity-40 group-hover:opacity-100 transition-opacity" />}
              </div>
              <h3 className="mt-8 font-display font-bold text-2xl text-white">{step.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-400">{step.d}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export const CtaBand = () => {
  const { t } = useLang();
  return (
    <section className="bg-white py-24 lg:py-28" data-testid="cta-band">
      <div className="mx-auto max-w-5xl px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="relative overflow-hidden rounded-md bg-emerald-500 px-8 py-14 lg:px-16 lg:py-20"
        >
          <div className="absolute inset-0 grain-overlay opacity-20" />
          <div className="relative max-w-2xl">
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#04170F]">{t.cta.title}</h2>
            <p className="mt-5 text-base lg:text-lg text-[#04170F]/80 max-w-xl">{t.cta.subtitle}</p>
            <a href="/register" className="group mt-9 inline-flex items-center gap-2 rounded-full bg-[#0A0F1A] pl-6 pr-5 py-3.5 text-sm font-semibold text-white hover:bg-[#0F172A] transition-colors duration-300" data-testid="cta-band-button">
              {t.cta.button}
              <ArrowRight size={18} weight="bold" className="group-hover:translate-x-1 transition-transform duration-300" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
